import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Trash2, Save, BookOpen, Sliders, Database, Sparkles, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  return (
    <div className="animate-fade-up">
      <div className="mb-5">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">Company & settings</h1>
        <p className="mt-1 text-sm text-stone-500">Approved facts the copilot may use, and follow-up configuration.</p>
      </div>
      <Tabs defaultValue="facts">
        <TabsList>
          <TabsTrigger value="facts" data-testid="settings-tab-facts"><BookOpen className="mr-1.5 h-4 w-4" /> Company facts</TabsTrigger>
          <TabsTrigger value="config" data-testid="settings-tab-config"><Sliders className="mr-1.5 h-4 w-4" /> Configuration</TabsTrigger>
        </TabsList>
        <TabsContent value="facts" className="mt-4"><CompanyFacts /></TabsContent>
        <TabsContent value="config" className="mt-4">
          <div className="space-y-5">
            <Config />
            <DataTools />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CompanyFacts() {
  const qc = useQueryClient();
  const { data: facts = [], isLoading } = useQuery({ queryKey: ["facts"], queryFn: async () => (await api.get("/company-facts")).data });
  const [category, setCategory] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editCategory, setEditCategory] = useState("");
  const [editContent, setEditContent] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const add = async () => {
    if (!category.trim() || !content.trim()) { toast.error("Category and content required"); return; }
    setSaving(true);
    try {
      await api.post("/company-facts", { category, content, active: true });
      toast.success("Fact added"); setCategory(""); setContent("");
      qc.invalidateQueries({ queryKey: ["facts"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { await api.delete(`/company-facts/${id}`); toast.success("Fact removed"); qc.invalidateQueries({ queryKey: ["facts"] }); };

  const restoreDefaults = async () => {
    setRestoring(true);
    try {
      const r = await api.post("/company-facts/restore-defaults");
      const n = r.data.added;
      toast.success(n > 0 ? `Restored ${n} default fact${n > 1 ? "s" : ""}` : "All 8 default facts are already present");
      qc.invalidateQueries({ queryKey: ["facts"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRestoring(false); }
  };

  const openEdit = (f) => { setEditing(f); setEditCategory(f.category || ""); setEditContent(f.content || ""); };
  const saveEdit = async () => {
    if (!editCategory.trim() || !editContent.trim()) { toast.error("Category and content required"); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/company-facts/${editing.id}`, { category: editCategory, content: editContent });
      toast.success("Fact updated"); setEditing(null);
      qc.invalidateQueries({ queryKey: ["facts"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-5">
        <h3 className="mb-3 font-heading font-semibold text-stone-900">Add approved fact</h3>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Category</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Sourcing, Sample policy" data-testid="fact-category-input" /></div>
          <div className="space-y-1.5"><Label>Content</Label><Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={4} placeholder="Only verified, approved information" data-testid="fact-content-input" /></div>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={add} disabled={saving} data-testid="fact-add-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1.5 h-4 w-4" /> Add fact</>}</Button>
        </div>
        <div className="mt-4 border-t border-stone-200 pt-4">
          <p className="mb-2 text-xs text-stone-500">Deleted your standard facts? Bring back the 8 built-in default facts. Existing facts are untouched — only missing defaults are added.</p>
          <Button variant="outline" onClick={restoreDefaults} disabled={restoring} data-testid="fact-restore-defaults-button">{restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-1.5 h-4 w-4" /> Restore default 8 facts</>}</Button>
        </div>
      </div>
      <div className="space-y-2">
        {isLoading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div> :
          facts.map((f) => (
            <div key={f.id} className="rounded-xl border border-stone-200 bg-white p-3" data-testid={`fact-${f.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-gold">{f.category}</div>
                  <p className="mt-1 text-sm text-stone-700">{f.content}</p>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button size="sm" variant="ghost" className="text-stone-500 hover:text-forest" onClick={() => openEdit(f)} data-testid={`fact-edit-${f.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(f.id)} data-testid={`fact-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent data-testid="fact-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit company fact</DialogTitle>
            <DialogDescription>Only verified, approved information the copilot may use.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Category</Label><Input value={editCategory} onChange={(e) => setEditCategory(e.target.value)} placeholder="e.g. Sourcing, Sample policy" data-testid="fact-edit-category-input" /></div>
            <div className="space-y-1.5"><Label>Content</Label><Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={4} data-testid="fact-edit-content-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="fact-edit-cancel">Cancel</Button>
            <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={saveEdit} disabled={savingEdit} data-testid="fact-edit-save">{savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save changes</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Config() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  if (!form) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const companyLine = [form.company_name, form.company_website, form.company_phone].filter(Boolean).join(" | ");
  const addressBlock = (form.company_address || "").trim();
  const sigPreview = ["— [Salesperson's name] | [Salesperson's number]", companyLine, addressBlock].filter(Boolean).join("\n");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", {
        max_unanswered_followups: parseInt(form.max_unanswered_followups, 10),
        default_followup_days: parseInt(form.default_followup_days, 10),
        session_timeout_minutes: parseInt(form.session_timeout_minutes, 10),
        default_country_code: form.default_country_code,
        idle_days: parseInt(form.idle_days, 10),
        auto_dormant_enabled: !!form.auto_dormant_enabled,
        auto_dormant_days: parseInt(form.auto_dormant_days, 10),
        company_name: form.company_name,
        company_website: form.company_website,
        company_phone: form.company_phone,
        company_address: form.company_address,
      });
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-5">
        <h3 className="mb-4 font-heading font-semibold text-stone-900">Company contact details</h3>
        <p className="-mt-2 mb-4 text-xs text-stone-500">Included in the signature of every generated message.</p>
        <div className="grid grid-cols-2 gap-4">
          <F label="Company name"><Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} data-testid="setting-company-name" /></F>
          <F label="Website"><Input value={form.company_website || ""} onChange={(e) => set("company_website", e.target.value)} data-testid="setting-company-website" /></F>
          <F label="Company phone"><Input value={form.company_phone || ""} onChange={(e) => set("company_phone", e.target.value)} data-testid="setting-company-phone" /></F>
        </div>
        <div className="mt-4">
          <F label="Company address (one line per row)">
            <Textarea rows={4} value={form.company_address || ""} onChange={(e) => set("company_address", e.target.value)} placeholder={"Prime Gold Tea Pvt. Ltd.\nM. R. Road, Siliguri\nWest Bengal 734005"} data-testid="setting-company-address" />
          </F>
        </div>
        <div className="mt-4 rounded-lg border border-stone-200 bg-whatsapp-bg/30 p-3" data-testid="signature-preview">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-stone-400">Signature preview</div>
          <pre className="whitespace-pre-wrap font-sans text-sm text-stone-700">{sigPreview}</pre>
          <p className="mt-1.5 text-[11px] text-stone-400">Each salesperson's own name &amp; number (set under the user menu → <strong>My details</strong>) appear at the top of their messages. The company block and address below are shared by everyone.</p>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-5">
        <h3 className="mb-4 font-heading font-semibold text-stone-900">Follow-up configuration</h3>
        <div className="grid grid-cols-2 gap-4">
          <F label="Max unanswered follow-ups"><Input type="number" value={form.max_unanswered_followups} onChange={(e) => set("max_unanswered_followups", e.target.value)} data-testid="setting-max-followups" /></F>
          <F label="Default follow-up (days)"><Input type="number" value={form.default_followup_days} onChange={(e) => set("default_followup_days", e.target.value)} data-testid="setting-default-days" /></F>
          <F label="Session timeout (minutes)"><Input type="number" value={form.session_timeout_minutes} onChange={(e) => set("session_timeout_minutes", e.target.value)} data-testid="setting-session-timeout" /></F>
          <F label="Default country code"><Input value={form.default_country_code} onChange={(e) => set("default_country_code", e.target.value)} data-testid="setting-country-code" /></F>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-5">
        <h3 className="mb-1 font-heading font-semibold text-stone-900">Idle customers & auto-dormant</h3>
        <p className="mb-4 text-xs text-stone-500">Controls when a customer is flagged "idle" on the Customers tab, and optionally auto-moves long-untouched customers to Dormant each day.</p>
        <div className="grid grid-cols-2 gap-4">
          <F label="Flag as idle after (days with no contact)"><Input type="number" min="1" value={form.idle_days ?? 7} onChange={(e) => set("idle_days", e.target.value)} data-testid="setting-idle-days" /></F>
          <F label="Auto-dormant after (days with no contact)"><Input type="number" min="1" value={form.auto_dormant_days ?? 30} onChange={(e) => set("auto_dormant_days", e.target.value)} disabled={!form.auto_dormant_enabled} data-testid="setting-auto-dormant-days" /></F>
        </div>
        <label className="mt-4 flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2.5">
          <span className="text-sm text-stone-600">Auto-move idle customers to Dormant daily <span className="block text-[11px] text-stone-400">Runs once a day. They stay in the separate Dormant list and can be reactivated anytime.</span></span>
          <Switch checked={!!form.auto_dormant_enabled} onCheckedChange={(v) => set("auto_dormant_enabled", v)} data-testid="setting-auto-dormant-toggle" />
        </label>
      </div>

      <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={save} disabled={saving} data-testid="settings-save"><Save className="mr-1.5 h-4 w-4" /> Save settings</Button>
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs font-semibold text-stone-500">{label}</Label>{children}</div>;
}

function DataTools() {
  const qc = useQueryClient();
  const [seeding, setSeeding] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [wiping, setWiping] = useState(false);

  const refreshAll = () => {
    ["customers", "dashboard", "products", "templates", "facts", "reports", "audit", "followups", "users"]
      .forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  const addDemo = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/admin/seed-demo");
      const c = r.data.counts;
      toast.success(`Demo data loaded · ${c.customers} customers, ${c.products} products`);
      refreshAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSeeding(false); }
  };

  const wipe = async () => {
    if (!confirmed) { toast.error("Please confirm before wiping"); return; }
    setWiping(true);
    try {
      const r = await api.post("/admin/wipe", { confirm: true });
      toast.success(`Wiped ${r.data.total} records`);
      setWipeOpen(false); setConfirmed(false);
      refreshAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setWiping(false); }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-5">
      <h3 className="mb-1 font-heading font-semibold text-stone-900">Demo data tools</h3>
      <p className="mb-4 text-xs text-stone-500">Load sample customers, products, templates and company facts — or wipe all business data. User accounts and settings are kept.</p>
      <div className="flex flex-wrap gap-2">
        <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={addDemo} disabled={seeding} data-testid="add-demo-button">
          {seeding ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />} Add demo data
        </Button>
        <Button variant="destructive" onClick={() => { setConfirmed(false); setWipeOpen(true); }} data-testid="wipe-data-button">
          <Trash2 className="mr-1.5 h-4 w-4" /> Wipe all data
        </Button>
      </div>

      <Dialog open={wipeOpen} onOpenChange={setWipeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" /> Wipe all data</DialogTitle>
            <DialogDescription>This permanently deletes all customers, communications, follow-ups, products, templates, company facts and audit logs. User accounts and settings are preserved. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 cursor-pointer">
            <Checkbox checked={confirmed} onCheckedChange={setConfirmed} data-testid="wipe-confirm-checkbox" className="mt-0.5" />
            <span className="text-sm text-red-700">I understand this will permanently delete all business data.</span>
          </label>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWipeOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={wipe} disabled={wiping || !confirmed} data-testid="wipe-confirm-button">
              {wiping ? <Loader2 className="h-4 w-4 animate-spin" /> : "Wipe everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
